// The room's own-score revision (D9, CR-027): the author revises their own ratings and recommendation in a revealed
// room, below VP, before the candidate's outcome. At VP+ and on a legacy requisition the button stays, blocked, with
// his rule sentence; the original stays the room's evidence and the revision keeps its rationale.
// B14 (C2, C6): the revised recommendation is chosen from the room's offered set (`debrief.open.level.offered`) in the
// mapping's words; Consider Other Role asks *Suggested role* (required) and *Why?* under the form, sent with the
// revision (the server stores the answer 'yes'; at VP+ it binds as No Hire with the role carried).
import { useState } from "react";
import { ACTION_LABELS, Button, COMMON_LABELS, Dialog, DiffRevise, Field, Input, PLAYBOOK_LABELS, TextArea } from "@/mc";
import type { DiffReviseRow, RatingValue } from "@/mc";
import { scorecardReviseInRoom, type RatingInput, type ScorecardReviseInRoomInput } from "@/api/tools";
import { errorText, useWrite } from "@/api/useTool";
import { levelOf, recoWord } from "@/views/b7/room/reco";
import type { RoomPanelProps } from "@/views/b7/room/types";
import { bandOf, DECIDE_RULES, memberOf, reasonOk, ROOM_KEYS } from "./labels";

/** A row key is `<value|skill>:<dimension key>`; a revised rating is sent as that dimension's row, nothing else. */
export function ratingInputs(revised: Readonly<Record<string, RatingValue>>): RatingInput[] {
  return Object.entries(revised).flatMap(([key, value]) => {
    const [type, dimension] = key.split(":", 2);
    if ((type !== "value" && type !== "skill") || !dimension) return [];
    return [value === "na"
      ? { dimension_type: type, dimension_key: dimension, state: "not_assessed" as const }
      : { dimension_type: type, dimension_key: dimension, state: "scored" as const, score: value }];
  });
}

/** Consider Other Role asks the role it suggests. */
export const needsSuggestedRole = (reco: string | null) => reco === "consider_other_role";

/** The card's rows when the room's projection carries them ({key, label, value}); none: the recommendation alone. */
function rowsOf(card: Record<string, unknown>): { key: string; label: string; value: RatingValue | null }[] {
  const raw = card.ratings;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((r: unknown) => {
    if (!r || typeof r !== "object") return [];
    const row = r as { key?: unknown; label?: unknown; value?: unknown };
    if (typeof row.key !== "string") return [];
    const v = row.value;
    const value = v === "na" || (typeof v === "number" && v >= 1 && v <= 5) ? (v as RatingValue) : null;
    return [{ key: row.key, label: typeof row.label === "string" ? row.label : row.key, value }];
  });
}

export function ReviseDialog({ view, applicationId, onChanged, scorecardId }: RoomPanelProps & { scorecardId?: string | undefined }) {
  const [open, setOpen] = useState(false);
  const [revised, setRevised] = useState<Record<string, RatingValue>>({});
  const [reco, setReco] = useState<string | null>(null);
  const [suggested, setSuggested] = useState("");
  const [why, setWhy] = useState("");
  const [rationale, setRationale] = useState("");
  const [touched, setTouched] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const write = useWrite(scorecardReviseInRoom, ROOM_KEYS);
  const member = memberOf(view, applicationId);
  const card = member?.my_scorecard ?? null;
  if (!member || !card) return null;
  const id = scorecardId ?? (typeof card.scorecard_id === "string" ? card.scorecard_id : "");
  const band = bandOf(view);
  // D9: below VP only, in a revealed room, before this candidate's outcome; the server's flag wins when it is sent.
  const flag = member.can.revise_in_room;
  const allowed = band === "below_vp" && view.status === "revealed" && member.outcome === null && flag !== false && id !== "";
  const blocked = allowed ? undefined : { reason: band === "vp_plus" ? `${DECIDE_RULES.reviseRule} ${DECIDE_RULES.vpStrict}` : DECIDE_RULES.reviseRule };
  const { offered } = levelOf(view);
  const original = recoWord(card.recommendation)?.value;
  const rows: DiffReviseRow[] = rowsOf(card).map((r) => ({ key: r.key, label: r.label, original: r.value, revised: revised[r.key] ?? null }));
  const recoChanged = reco !== null && reco !== original;
  const roleOwed = recoChanged && needsSuggestedRole(reco);
  const roleOk = !roleOwed || suggested.trim() !== "";
  const ready = reasonOk(rationale) && roleOk && (Object.keys(revised).length > 0 || recoChanged);
  const submit = async () => {
    setTouched(true);
    if (!ready) return;
    setRefused(null);
    try {
      // only what changes is sent: a recommendation left out keeps the card's, never cleared by a null
      const input: ScorecardReviseInRoomInput = {
        requisition_id: view.requisition_id,
        cycle_id: view.cycle_id,
        application_id: applicationId,
        scorecard_id: id,
        ratings: ratingInputs(revised),
        rationale: rationale.trim(),
      };
      if (recoChanged) {
        input.recommendation = reco as NonNullable<ScorecardReviseInRoomInput["recommendation"]>;
        if (roleOwed) {
          input.consider_other_role_suggested = suggested.trim();
          if (why.trim()) input.consider_other_role_why = why.trim();
        }
      }
      await write.mutateAsync(input);
      setOpen(false);
      onChanged();
    } catch (e) {
      setRefused(errorText(e));
    }
  };
  return (
    <>
      <Button  blocked={blocked} onClick={() => setOpen(true)}>{ACTION_LABELS.reviseOwnScore}</Button>
      <Dialog
        preset="reviseOwnScore"
        open={open && allowed}
        onClose={() => setOpen(false)}
        width={720}
        refused={refused ? { reason: refused } : undefined}
        dismissLabel={ACTION_LABELS.cancel}
        primary={{
          label: ACTION_LABELS.submit, onClick: () => void submit(), busy: write.isPending, hs: "room.revise.submit",
          blocked: ready ? undefined : { reason: DECIDE_RULES.revisionPreserves },
        }}
      >
        <DiffRevise
          rows={rows}
          onRate={(key, value) => setRevised((now) => ({ ...now, [key]: value }))}
          offered={offered}
          originalReco={original}
          revisedReco={reco}
          onReco={setReco}
          rationale={rationale}
          onRationale={setRationale}
          rationaleError={touched && !reasonOk(rationale) ? COMMON_LABELS.required : undefined}
        />
        {roleOwed ? (
          <>
            <Field label={PLAYBOOK_LABELS.suggestedRole} required error={touched && !roleOk ? COMMON_LABELS.required : undefined}>
              <Input value={suggested} onChange={(event) => setSuggested(event.target.value)} maxLength={200}  />
            </Field>
            <Field label={PLAYBOOK_LABELS.why}>
              <TextArea rows={2} value={why} onChange={(event) => setWhy(event.target.value)}  />
            </Field>
          </>
        ) : null}
      </Dialog>
    </>
  );
}
